import React, { useEffect, useState, useCallback } from "react";
import Header from "./Header";
import { Link } from "react-router-dom";
import { api } from "../server";

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(n)) ? Number(n) : 0);

function ddmmyyyy(d = new Date()) {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function Dashboard() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("TODAY");

  const fetchKpis = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/admin/dashboard");
      setKpis(data);
    } catch (err) {
      console.error("Dashboard KPI error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const today = kpis?.today || {};
  const month = kpis?.month || {};
  const counts = kpis?.counts || {};

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Dashboard</h3>
                <div className="text-secondary lh-1">
                  Overview • Orders • Revenue • Customers • Drivers
                </div>
              </div>
              <div className="d-flex gap-1 align-items-center">
                <div className="btn-group">
                  <button
                    type="button"
                    className={`btn border-0 px-4 ${range === "TODAY" ? "btn-primary text-white" : "btn-outline-secondary bg-white text-dark"}`}
                    onClick={() => setRange("TODAY")}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    className={`btn border-0 px-4 ${range === "MONTH" ? "btn-primary text-white" : "btn-outline-secondary bg-white text-dark"}`}
                    onClick={() => setRange("MONTH")}
                  >
                    This Month
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                  onClick={fetchKpis}
                  title="Refresh"
                >
                  <i className={`bi bi-arrow-clockwise me-2 ${loading ? "spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>

            {loading && !kpis ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" />
              </div>
            ) : (
              <div className="row g-2">
                {/* Orders KPI */}
                <div className="col-12 col-md-4">
                  <div className="card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body p-3 p-md-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                          style={{ width: 40, height: 40 }}
                        >
                          <i className="bi bi-cart" />
                        </div>
                        <div>
                          <div className="fw-semibold lh-1">Orders</div>
                          <div className="text-secondary small">
                            {range === "TODAY" ? `Today — ${ddmmyyyy()}` : "This Month"}
                          </div>
                        </div>
                      </div>
                      <div className="fs-4 fw-semibold">
                        {range === "TODAY" ? (today.orders || 0) : (month.orders || 0)}
                      </div>
                      <div className="small text-secondary mt-2">
                        Delivered —{" "}
                        <span className="fw-semibold text-success">
                          {range === "TODAY" ? (today.delivered || 0) : (month.delivered || 0)}
                        </span>
                        <br />
                        Pending —{" "}
                        <span className="fw-semibold text-warning">
                          {counts.pendingOrders ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue KPI */}
                <div className="col-12 col-md-4">
                  <div className="card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body p-3 p-md-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                          style={{ width: 40, height: 40 }}
                        >
                          <i className="bi bi-graph-up" />
                        </div>
                        <div>
                          <div className="fw-semibold lh-1">Revenue</div>
                          <div className="text-secondary small">From delivered orders</div>
                        </div>
                      </div>
                      <div className="fs-4 fw-semibold text-success">
                        {money(range === "TODAY" ? today.revenue : month.revenue)}
                      </div>
                      <div className="small text-secondary mt-2">
                        {range === "TODAY"
                          ? `Today's deliveries: ${today.delivered || 0}`
                          : `This month deliveries: ${month.delivered || 0}`}
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-3">
                        <Link to="/daily-report" className="btn btn-sm btn-primary rounded-3 px-4">
                          <i className="bi bi-clipboard-data me-1" />
                          Daily Report
                        </Link>
                        <Link to="/monthly-report" className="btn btn-sm btn-light rounded-3 px-4">
                          <i className="bi bi-calendar3 me-1" />
                          Monthly
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counts KPI */}
                <div className="col-12 col-md-4">
                  <div className="card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body p-3 p-md-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning"
                          style={{ width: 40, height: 40 }}
                        >
                          <i className="bi bi-people" />
                        </div>
                        <div>
                          <div className="fw-semibold lh-1">Active Counts</div>
                          <div className="text-secondary small">Customers • Drivers</div>
                        </div>
                      </div>
                      <div className="row g-2">
                        <div className="col-6">
                          <div className="border rounded-4 p-3 bg-white shadow-sm h-100">
                            <div className="text-secondary small lh-sm">Customers</div>
                            <div className="fw-semibold">{counts.totalCustomers ?? 0}</div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="border rounded-4 p-3 bg-white shadow-sm h-100">
                            <div className="text-secondary small lh-sm">Drivers</div>
                            <div className="fw-semibold">{counts.activeDrivers ?? 0}</div>
                          </div>
                        </div>
                      </div>
                      <div className="small text-secondary mt-3">
                        Pending Corporate —{" "}
                        <span className="fw-semibold text-danger">
                          {counts.pendingCorporate ?? 0}
                        </span>
                        <br />
                        Open Tickets —{" "}
                        <span className="fw-semibold">{counts.openTickets ?? 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Links Grid */}
            <div className="mt-3">
              <div className="row g-2">
                {[
                  { to: "/orders", icon: "bi-cart-check", label: "Orders", sub: "All customer orders", bg: "bg-primary-subtle", text: "text-primary" },
                  { to: "/customers", icon: "bi-people", label: "Customers", sub: "Individual & corporate", bg: "bg-info-subtle", text: "text-info" },
                  { to: "/corporate-approvals", icon: "bi-building-check", label: "Corporate Approvals", sub: `${counts.pendingCorporate ?? 0} pending`, bg: "bg-warning-subtle", text: "text-warning" },
                  { to: "/drivers", icon: "bi-truck", label: "Drivers", sub: `${counts.activeDrivers ?? 0} active`, bg: "bg-success-subtle", text: "text-success" },
                  { to: "/products", icon: "bi-droplet-half", label: "Products", sub: `${counts.totalProducts ?? 0} total`, bg: "bg-primary-subtle", text: "text-primary" },
                  { to: "/daily-report", icon: "bi-clipboard-data", label: "Daily Report", sub: "Today's deliveries", bg: "bg-info-subtle", text: "text-info" },
                  { to: "/monthly-report", icon: "bi-calendar3", label: "Monthly Report", sub: "Trends & top products", bg: "bg-success-subtle", text: "text-success" },
                  { to: "/notifications", icon: "bi-bell", label: "Notifications", sub: "Sent to users", bg: "bg-secondary-subtle", text: "text-secondary" },
                  { to: "/support", icon: "bi-headset", label: "Support Tickets", sub: `${counts.openTickets ?? 0} open`, bg: "bg-danger-subtle", text: "text-danger" },
                ].map((m) => (
                  <div className="col-6 col-md-4 col-lg-3" key={m.to}>
                    <Link to={m.to} className="text-decoration-none">
                      <div className="card border-0 shadow-sm rounded-4 h-100 module-card">
                        <div className="card-body p-3 d-flex align-items-center gap-2">
                          <div
                            className={`rounded-3 d-inline-flex align-items-center justify-content-center ${m.bg} ${m.text}`}
                            style={{ width: 44, height: 44 }}
                          >
                            <i className={`bi ${m.icon} fs-5`} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-semibold text-dark lh-1">{m.label}</div>
                            <div className="text-secondary small mt-1">{m.sub}</div>
                          </div>
                          <i className="bi bi-chevron-right text-secondary" />
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
      </div>
      <style>{`
        .module-card { transition: transform .15s ease, box-shadow .15s ease; }
        .module-card:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.08) !important; }
      `}</style>
    </>
  );
}

export default Dashboard;
